/**
 * Cookie Banner Component
 * GDPR compliant cookie consent banner
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Cookie, Settings, Shield } from 'lucide-react';
import { useCookieConsentContext } from '@/contexts/CookieConsentContext';
import { motion, AnimatePresence } from 'framer-motion';

export const CookieBanner: React.FC = () => {
  const { showBanner, acceptAll, rejectNonEssential, openPreferences } = useCookieConsentContext();

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-40 p-4 md:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-description"
        >
          <div className="container mx-auto max-w-4xl">
            <div className="bg-card border border-border shadow-lg p-6 md:p-8" dir="rtl">
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-primary/10 rounded-full">
                  <Cookie className="h-5 w-5 text-primary" />
                </div>
                <h2 id="cookie-banner-title" className="text-lg font-semibold text-foreground">
                  אנחנו משתמשים בעוגיות
                </h2>
              </div>

              {/* Description */}
              <p id="cookie-banner-description" className="text-muted-foreground text-sm mb-6 leading-relaxed">
                אנו משתמשים בעוגיות (Cookies) ובטכנולוגיות דומות כדי לשפר את חווית הגלישה שלך,
                להתאים אישית תוכן ופרסומות, לספק תכונות מדיה חברתית ולנתח את התנועה באתר.
                חלק מהעוגיות חיוניות לפעולה תקינה של האתר, בעוד שאחרות עוזרות לנו לשפר את השירות.
                <Link 
                  to="/cookie-policy" 
                  className="text-primary underline mr-1"
                  tabIndex={0}
                >
                  למידע נוסף
                </Link>
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={acceptAll}
                  className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
                  aria-label="אישור כל העוגיות"
                >
                  <Shield className="h-4 w-4 ml-2" />
                  אישור כל העוגיות
                </Button>
                
                <Button
                  onClick={rejectNonEssential}
                  variant="outline"
                  className="flex-1 border-border hover:bg-muted text-foreground hover:text-foreground"
                  aria-label="דחיית עוגיות לא חיוניות"
                >
                  דחיית עוגיות לא חיוניות
                </Button>
                
                <Button
                  onClick={openPreferences}
                  variant="ghost"
                  className="flex-1 hover:bg-muted text-foreground hover:text-foreground"
                  aria-label="ניהול העדפות עוגיות"
                >
                  <Settings className="h-4 w-4 ml-2" />
                  ניהול העדפות
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
